/**
 * PHOTON-80 — single source of truth for severity (design-spec §6.2, §10).
 *
 * Both the pillar chips and the action-list ranking read from here so a card
 * can never say "Watch" while its action row says "Critical". Chip labels are
 * decisions, not color names, per the spec ("Act now", never "RED").
 */

export type Severity = "ok" | "info" | "warn" | "crit";

/** Data state per source — discriminated union so the failed case cannot
 *  carry a current value (spec §8.3: the value slot must go empty). */
export type SourceState =
  | { kind: "loading" }
  | { kind: "fresh"; asOfUtc: string }
  | { kind: "stale"; lastGoodUtc: string; ageLabel: string; budgetLabel: string }
  | {
      kind: "failed";
      error: string;
      lastKnown: { label: string; asOfUtc: string; ageLabel: string };
    };

export const SEVERITY_CHIP_LABEL: Record<Severity, string> = {
  ok: "On track",
  info: "On plan",
  warn: "Watch",
  crit: "Act now",
};

/** Rank for sorting the action list — critical first (spec §6.6). */
export const SEVERITY_RANK: Record<Severity, number> = {
  crit: 0,
  warn: 1,
  info: 2,
  ok: 3,
};

// RAG thresholds live server-side in crm-service's executive_thresholds.py —
// the ONE authoritative module (acceptance criterion). The aggregate endpoint
// stamps a severity on every pillar and action; this file only maps those
// severities to labels and ranking. Do not re-derive severity in the portal.
